'use client';

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Markdown from 'markdown-to-jsx';
import { Zap, Loader2, Lock, CheckCircle2 } from 'lucide-react';
import MultipleChoiceMechanic from './mechanics/MultipleChoiceMechanic';
import QuickClassificationMechanic from './mechanics/QuickClassificationMechanic';
import MissingStepMechanic from './mechanics/MissingStepMechanic';
import SequenceBuilderMechanic from './mechanics/SequenceBuilderMechanic';
import ParameterTunerMechanic from './mechanics/ParameterTunerMechanic';
import PredictionMechanic from './mechanics/PredictionMechanic';
import EvidenceMatchMechanic from './mechanics/EvidenceMatchMechanic';
import DiagnosticLabMechanic from './mechanics/DiagnosticLabMechanic';
import ConstraintConstructionMechanic from './mechanics/ConstraintConstructionMechanic';
import TradeoffDecisionMechanic from './mechanics/TradeoffDecisionMechanic';
import SystemSimulationMechanic from './mechanics/SystemSimulationMechanic';
import ComplexSystemRepairMechanic from './mechanics/ComplexSystemRepairMechanic';
import AbstractTransferMechanic from './mechanics/AbstractTransferMechanic';
import { getLessonMechanicsClient } from '@/app/actions/gamification';
import type { LessonTierVariant } from '@/lib/gamification/contracts/lesson-variants';
import type { LessonTierState } from '@/lib/gamification/services/lesson-tier-state';
import TtsPlayer from './TtsPlayer';

type LessonMechanicsLoader = (lessonSlug: string, selectedTier?: string) => Promise<any>;

function normalizeAnchorHeading(value: string | null | undefined): string | null {
  if (!value) return null;
  return value
    .replace(/^h[1-6]#/, '')
    .replace(/^#{1,6}\s+/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

const GameMechanicPlaceholder = ({ type, ...props }: any) => {
  return (
    <div className="my-6 p-4 border border-primary/50 bg-primary/5 rounded-xl shadow-sm flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
        <Zap className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-bold text-sm uppercase tracking-wider text-primary">Interactive Mechanic: {type}</h4>
        <p className="text-xs text-muted-foreground mt-1 truncate">
          Props: {JSON.stringify(props)}
        </p>
      </div>
    </div>
  );
};

const GameMechanicRouter = ({ type, ...props }: any) => {
  switch (type) {
    case 'multiple-choice':
      return <MultipleChoiceMechanic {...props} />;
    case 'quick-classification':
      return <QuickClassificationMechanic {...props} />;
    case 'missing-step':
      return <MissingStepMechanic {...props} />;
    case 'sequence-builder':
      return <SequenceBuilderMechanic {...props} />;
    case 'parameter-tuner':
      return <ParameterTunerMechanic {...props} />;
    case 'prediction':
      return <PredictionMechanic {...props} />;
    case 'evidence-match':
      return <EvidenceMatchMechanic {...props} />;
    case 'diagnostic-lab':
      return <DiagnosticLabMechanic {...props} />;
    case 'constraint-construction':
      return <ConstraintConstructionMechanic {...props} />;
    case 'tradeoff-decision':
      return <TradeoffDecisionMechanic {...props} />;
    case 'system-simulation':
      return <SystemSimulationMechanic {...props} />;
    case 'complex-system-repair':
      return <ComplexSystemRepairMechanic {...props} />;
    case 'abstract-transfer':
      return <AbstractTransferMechanic {...props} />;
    default:
      return <GameMechanicPlaceholder type={type} {...props} />;
  }
};

interface LessonEngineProps {
  content: string;
  lessonSlug: string;
  fontSizeLevel?: number;
  fontFamily?: string;
  loadLessonMechanics?: LessonMechanicsLoader;
  persistLocalProgress?: boolean;
  selectedTier?: LessonTierVariant | null;
  onSelectedTierChange?: (tier: LessonTierVariant | null) => void;
  onTierStateChange?: (meta: {
    selectedTier: LessonTierVariant | null;
    availableTiers: LessonTierVariant[];
    tierStates: LessonTierState[];
  }) => void;
  showTierControlsInBody?: boolean;
}

export default function LessonEngine({
  content,
  lessonSlug,
  fontSizeLevel = 1,
  fontFamily = 'inter',
  loadLessonMechanics,
  persistLocalProgress = false,
  selectedTier: controlledSelectedTier,
  onSelectedTierChange,
  onTierStateChange,
  showTierControlsInBody = true,
}: LessonEngineProps) {
  const onTierStateChangeRef = useRef(onTierStateChange);
  useEffect(() => {
    onTierStateChangeRef.current = onTierStateChange;
  }, [onTierStateChange]);
  const [mechanicsPayload, setMechanicsPayload] = useState<any>(null);
  const [isLoadingMechanics, setIsLoadingMechanics] = useState(true);
  const [internalSelectedTier, setInternalSelectedTier] = useState<LessonTierVariant | null>(null);
  const [availableTiers, setAvailableTiers] = useState<LessonTierVariant[]>([]);
  const [variantLessonSlug, setVariantLessonSlug] = useState<string>(lessonSlug);
  const [tierStates, setTierStates] = useState<LessonTierState[]>([]);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const hasResolvedMechanicsRef = useRef(false);
  const selectedTier = controlledSelectedTier ?? internalSelectedTier;

  const updateSelectedTier = (nextTier: LessonTierVariant | null) => {
    if (controlledSelectedTier === undefined) {
      setInternalSelectedTier(nextTier);
    }
    onSelectedTierChange?.(nextTier);
  };

  useEffect(() => {
    setMechanicsPayload(null);
    setInternalSelectedTier(null);
    setAvailableTiers([]);
    setVariantLessonSlug(lessonSlug);
    setTierStates([]);
    hasResolvedMechanicsRef.current = false;
    setIsLoadingMechanics(true);
  }, [lessonSlug]);

  useEffect(() => {
    const handleMechanicSolved = () => setRefreshNonce((previous) => previous + 1);
    window.addEventListener('classes:lesson-mechanic-solved', handleMechanicSolved);
    return () => window.removeEventListener('classes:lesson-mechanic-solved', handleMechanicSolved);
  }, []);

  useEffect(() => {
    async function fetchMechanics() {
      const shouldBlockRender = !hasResolvedMechanicsRef.current;
      if (shouldBlockRender) {
        setIsLoadingMechanics(true);
      }
      const loader = loadLessonMechanics || getLessonMechanicsClient;
      try {
        const res = await loader(lessonSlug, selectedTier || undefined);
        if (res.success && res.mechanics?.mechanics) {
          setMechanicsPayload(res.mechanics.mechanics);
          setVariantLessonSlug(res.mechanics.variantLessonSlug || lessonSlug);
          setAvailableTiers(res.mechanics.availableTiers || []);
          setTierStates(res.mechanics.tierStates || []);
          hasResolvedMechanicsRef.current = true;
          if (res.mechanics.selectedTier && res.mechanics.selectedTier !== selectedTier) {
            updateSelectedTier(res.mechanics.selectedTier);
          }
        } else {
          if (!hasResolvedMechanicsRef.current) {
            setMechanicsPayload(null);
            setTierStates([]);
          }
        }
      } catch (error) {
        console.warn('[LessonEngine] Failed to load mechanics. You might be offline.', error);
        if (!hasResolvedMechanicsRef.current) {
          setMechanicsPayload(null);
          setTierStates([]);
        }
      }
      setIsLoadingMechanics(false);
    }
    fetchMechanics();
  }, [lessonSlug, selectedTier, refreshNonce]);

  useEffect(() => {
    onTierStateChangeRef.current?.({
      selectedTier,
      availableTiers,
      tierStates,
    });
  }, [selectedTier, availableTiers, tierStates]);

  const { mechanicsByAnchor, mechanicsById } = useMemo(() => {
    const anchorMap: Record<string, any[]> = {};
    const byId: Record<string, any> = {};
    if (mechanicsPayload) {
      Object.entries(mechanicsPayload).forEach(([mechanicId, mech]: [string, any]) => {
        byId[mechanicId] = { ...mech, mechanicId, lessonSlug, variantLessonSlug };
        const anchor = normalizeAnchorHeading(
          mech.anchorId
          || mech.anchorHeading
          || mech.anchor?.heading
          || mech.publicPayload?.anchor?.heading
          || mech.payload?.anchor?.heading
        );
        if (anchor) {
          if (!anchorMap[anchor]) anchorMap[anchor] = [];
          anchorMap[anchor].push(byId[mechanicId]);
        }
      });
    }
    return {
      mechanicsByAnchor: anchorMap,
      mechanicsById: byId,
    };
  }, [mechanicsPayload, lessonSlug, variantLessonSlug]);

  // Strip YAML frontmatter and authoring-only skip markers before rendering.
  const cleanContent = content
    .replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, '')
    .replace(/^\s*%%\s*skip\s*%%\s*$/gim, '');

  // Pre-process markdown to inject mechanics at the END of sections
  const lines = cleanContent.split('\n');
  const processedLines = [];
  let currentHeadingId = null;
  const mechanicsQueueByAnchor = Object.fromEntries(
    Object.entries(mechanicsByAnchor).map(([anchor, mechanics]) => [anchor, [...mechanics]])
  ) as Record<string, any[]>;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const headingMatch = line.match(/^(#{1,6})\s+(.+)/);
    const hrMatch = line.match(/^---\s*$/);

    if (headingMatch || hrMatch) {
      // Inject ONE mechanic for the previous heading (FIFO: first mechanic goes to first heading occurrence)
      if (currentHeadingId && mechanicsQueueByAnchor[currentHeadingId]?.length) {
        const mech = mechanicsQueueByAnchor[currentHeadingId].shift()!;
        processedLines.push(`\n<gamemechanic id="${mech.mechanicId}"></gamemechanic>\n`);
        currentHeadingId = null; // clear to prevent double-injection on adjacent heading+HR
      }

      if (headingMatch) {
        const text = headingMatch[2];
        currentHeadingId = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }
    }

    processedLines.push(line);
  }

  // Inject trailing mechanic for the last section
  if (currentHeadingId && mechanicsQueueByAnchor[currentHeadingId]?.length) {
    const mech = mechanicsQueueByAnchor[currentHeadingId].shift()!;
    processedLines.push(`\n<gamemechanic id="${mech.mechanicId}"></gamemechanic>\n`);
  }
  // Inject any remaining mechanics at the end (for headings that appeared more times than mechanics)
  for (const key of Object.keys(mechanicsQueueByAnchor)) {
    for (const mech of mechanicsQueueByAnchor[key]) {
      processedLines.push(`\n<gamemechanic id="${mech.mechanicId}"></gamemechanic>\n`);
    }
    delete mechanicsQueueByAnchor[key];
  }

  const processedContent = processedLines.join('\n');

  const fontClasses = ['prose-sm', 'prose-base', 'prose-lg', 'prose-xl'];
  const activeFontClass = fontClasses[Math.min(Math.max(fontSizeLevel, 0), 3)] || 'prose-base';
  const fontClassMap: Record<string, string> = {
    inter: 'font-inter',
    merriweather: 'font-merriweather',
    'jetbrains-mono': 'font-mono-custom',
    playfair: 'font-playfair',
  };
  const activeFontFamily = fontClassMap[fontFamily] || 'font-inter';

  // Custom component for markdown-to-jsx
  const GameMechanicInjector = useCallback(({ id }: any) => {
    const mech = mechanicsById[id] || mechanicsPayload?.[id];
    if (!mech) return null;
    const hasHints = !!(mech.privateValidator?.hints?.length > 0);
    const localHints = mech.privateValidator?.hints?.map((h: any) => ({ id: h.id, hint: h.hint })) || [];
    const payload = {
      ...(mech.payload || mech.publicPayload || mech.publicPayloadRef || mech.data || mech),
      _hasHints: hasHints || !!mech.hasHints,
      _variantLessonSlug: variantLessonSlug,
      _selectedLessonTier: selectedTier,
      _persistLocalSolve: persistLocalProgress,
      _tierMechanicIds: Object.keys(mechanicsPayload || {}),
    };
    return (
      <div className="not-prose my-10 relative z-10 w-full animate-in slide-in-from-bottom-4 duration-700 fade-in fill-mode-both" data-mechanic-zoom={fontSizeLevel}>
        <GameMechanicRouter 
           type={mech.type} 
           {...mech} 
           payload={payload}
           localHints={localHints}
           localValidator={mech.privateValidator}
        />
      </div>
    );
  }, [mechanicsById, mechanicsPayload, variantLessonSlug, selectedTier, persistLocalProgress, fontSizeLevel]);

  const HeadingRenderer = useCallback(({ children, level, id, ...props }: any) => {
    const Tag = `h${level}` as keyof JSX.IntrinsicElements;
    const rawText = Array.isArray(children) ? children[0] : children;
    const generatedId = id || (typeof rawText === 'string' 
      ? rawText.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') 
      : '');

    return <Tag id={generatedId} className="scroll-m-20" {...props}>{children}</Tag>;
  }, []);

  const markdownOptions = useMemo(() => ({
    overrides: {
      gamemechanic: { component: GameMechanicInjector },
      h1: { component: HeadingRenderer, props: { level: 1 } },
      h2: { component: HeadingRenderer, props: { level: 2 } },
      h3: { component: HeadingRenderer, props: { level: 3 } },
      h4: { component: HeadingRenderer, props: { level: 4 } },
      h5: { component: HeadingRenderer, props: { level: 5 } },
      h6: { component: HeadingRenderer, props: { level: 6 } },
    },
  }), [GameMechanicInjector, HeadingRenderer]);

  if (isLoadingMechanics) {
    return (
      <div className="flex flex-col items-center justify-center py-20 opacity-50">
        <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
        <p className="text-sm">Initializing Gamification Engine...</p>
      </div>
    );
  }

  return (
    <div className={`w-full break-words [overflow-wrap:anywhere] prose dark:prose-invert max-w-none ${activeFontClass} ${activeFontFamily}
      prose-headings:text-primary prose-a:text-blue-500 hover:prose-a:text-blue-400
      prose-code:bg-muted prose-code:px-1 prose-code:rounded
      prose-pre:bg-muted/50 prose-pre:border prose-pre:border-border/50 prose-pre:overflow-x-auto prose-pre:max-w-full
      leading-relaxed`}>
      <div className="not-prose">
        <TtsPlayer content={content} />
      </div>
      {showTierControlsInBody && selectedTier && (
        <div className="not-prose mb-8 flex flex-wrap items-center gap-2 rounded-2xl border border-border/50 bg-muted/20 p-3">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Lesson Tier</span>
          {(tierStates.length > 0
            ? tierStates
            : (availableTiers.length > 0 ? availableTiers : [selectedTier]).map((tierOption) => ({
                tier: tierOption,
                isUnlocked: true,
                isCompleted: false,
              }))
          ).map((state: any) => (
            <button
              key={state.tier}
              type="button"
              onClick={() => state.isUnlocked && updateSelectedTier(state.tier)}
              disabled={!state.isUnlocked || tierStates.length <= 1}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                selectedTier === state.tier
                  ? 'border-primary bg-primary text-primary-foreground'
                  : state.isUnlocked
                  ? 'border-border/60 bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
                  : 'border-border/40 bg-muted/40 text-muted-foreground/60 cursor-not-allowed'
              }`}
              title={state.isUnlocked ? state.tier : `${state.tier} is locked until the previous tier is completed.`}
            >
              <span>{state.tier}</span>
              {state.isCompleted ? <CheckCircle2 className="w-3.5 h-3.5" /> : null}
              {!state.isUnlocked ? <Lock className="w-3.5 h-3.5" /> : null}
            </button>
          ))}
        </div>
      )}
      <Markdown
        options={markdownOptions}
      >
        {processedContent}
      </Markdown>
    </div>
  );
}
